import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PaymentMethods = ({ paymentMethods, onManageBilling, onRefresh }) => {
  const getCardBrandIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'CreditCard';
      case 'mastercard':
        return 'CreditCard';
      case 'amex':
        return 'CreditCard';
      default:
        return 'CreditCard';
    }
  };

  const formatExpiry = (month, year) => {
    if (!month || !year) return 'N/A';
    return `${String(month)?.padStart(2, '0')}/${String(year)?.slice(-2)}`;
  };

  const isExpired = (month, year) => {
    if (!month || !year) return false;
    const now = new Date();
    const expiry = new Date(year, month - 1);
    return expiry < now;
  };

  if (!paymentMethods || paymentMethods?.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Payment Methods</h2>
          <p className="text-muted-foreground">Manage your payment methods and billing information</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="CreditCard" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Payment Methods</h3>
          <p className="text-muted-foreground mb-6">
            Add a payment method to manage your subscription billing
          </p>
          <Button 
            onClick={onManageBilling}
            iconName="Plus"
            iconPosition="left"
          >
            Add Payment Method
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Payment Methods</h2>
          <p className="text-muted-foreground">Manage your payment methods and billing information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={onRefresh}
            iconName="RefreshCw"
            iconPosition="left"
            variant="outline"
          >
            Refresh
          </Button>
          <Button 
            onClick={onManageBilling}
            iconName="Plus"
            iconPosition="left"
          >
            Add Payment Method
          </Button>
        </div>
      </div>

      {/* Payment Methods List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {paymentMethods?.map((method) => {
          const expired = isExpired(method?.cardExpMonth, method?.cardExpYear);
          
          return (
            <div
              key={method?.id}
              className={`bg-card border rounded-lg p-6 ${
                method?.isDefault ? 'border-primary shadow-md' : 'border-border'
              }`}
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={getCardBrandIcon(method?.cardBrand)} size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold capitalize">
                      {method?.cardBrand || method?.type}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      •••• {method?.cardLastFour}
                    </p>
                  </div>
                </div>
                {method?.isDefault && (
                  <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                    Default
                  </span>
                )}
              </div>

              {/* Card Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Expires</span>
                  <span className={expired ? 'text-red-600 font-medium' : 'font-medium'}>
                    {formatExpiry(method?.cardExpMonth, method?.cardExpYear)}
                    {expired && ' (Expired)'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium capitalize">{method?.type}</span>
                </div>
              </div>

              {/* Expiry Warning */}
              {expired && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertTriangle" size={16} className="text-red-600 mt-0.5" />
                    <p className="text-xs text-red-700">
                      This card has expired. Please update your payment method to avoid service interruption.
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-border">
                <Button
                  onClick={onManageBilling}
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  iconName="Settings"
                  iconPosition="left"
                >
                  Manage Card
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Security Notice */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div className="space-y-2">
            <p className="font-medium">Secure Payment Processing</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• All payment information is encrypted and securely stored by Stripe</li>
              <li>• We never store your full card details on our servers</li>
              <li>• PCI DSS compliant payment processing</li>
              <li>• Automatic card updates for expired cards (where supported)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Billing Portal Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 mb-1">Manage Payment Methods</p>
            <p className="text-sm text-blue-700 mb-3">
              Use the Stripe billing portal to add, remove, or update your payment methods securely.
            </p>
            <Button
              onClick={onManageBilling}
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
            >
              Open Billing Portal
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;